/**
 * @fileoverview Arama API Islemleri Hook'u
 *
 * Bu modul, Emlakjet API'si ile arama, sayfalama ve bolgesel fiyat
 * bilgisi getirme islemlerini yoneten hook'u icerir.
 *
 * @module hooks/useSearchAPI
 */

import { useState, useCallback } from "react";
import {
  extractStructuredContent,
  isCallToolAvailable,
  ERROR_MESSAGES,
  LIMITS,
} from "../utils";

/**
 * @typedef {Object} UseSearchAPIReturn
 * @property {boolean} isCalling - Arama devam ediyor mu
 * @property {string|null} callError - Hata mesaji
 * @property {Function} setCallError - Hata mesaji set fonksiyonu
 * @property {boolean} isLoadingMore - Daha fazla yukleniyor mu
 * @property {boolean} regionLoading - Bolge fiyati yukleniyor mu
 * @property {Function} executeSearch - Arama calistir
 * @property {Function} executeLoadMore - Daha fazla yukle
 * @property {Function} executeRegionPrice - Bolge fiyati getir
 */

/**
 * Arama API islemleri hook'u.
 *
 * Bu hook, Emlakjet API'sine yapilan tum aramalari yonetir.
 * Her API cagrisi icin yukleme durumu ve hata yonetimi saglar.
 *
 * NOT: Bu hook sadece API cagrilarini yonetir. State guncelleme,
 * gecmis yonetimi ve bildirimler useListingSearch'te orchestrate edilir.
 *
 * @function useSearchAPI
 * @returns {UseSearchAPIReturn} API islem fonksiyonlari ve durumlar
 *
 * @example
 * const {
 *   isCalling,
 *   callError,
 *   executeSearch,
 *   executeLoadMore,
 *   executeRegionPrice,
 * } = useSearchAPI();
 *
 * const result = await executeSearch({
 *   path: "satilik-konut/istanbul",
 *   filters: "oda-sayisi=3-1",
 * });
 */
export function useSearchAPI() {
  const [isCalling, setIsCalling] = useState(false);
  const [callError, setCallError] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [regionLoading, setRegionLoading] = useState(false);

  /**
   * Arama calistir.
   *
   * @param {Object} params - Arama parametreleri
   * @param {string} params.path - Arama yolu
   * @param {string} [params.filters] - Filtre string'i
   * @param {string} [params.sort] - Siralama
   * @returns {Promise<Object|null>} Yapilandirilmis sonuc veya null
   */
  const executeSearch = useCallback(async ({ path, filters, sort }) => {
    if (!isCallToolAvailable()) {
      setCallError(ERROR_MESSAGES.CALL_TOOL_UNAVAILABLE);
      return null;
    }

    const normalizedPath = typeof path === "string" ? path.trim() : "";
    if (!normalizedPath) {
      setCallError(ERROR_MESSAGES.PATH_REQUIRED_VERBOSE);
      return null;
    }

    const normalizedFilters =
      typeof filters === "string" && filters.trim().length > 0
        ? filters.trim()
        : undefined;

    const normalizedSort =
      typeof sort === "string" && sort.trim().length > 0
        ? sort.trim()
        : undefined;

    setIsCalling(true);
    setCallError(null);

    try {
      const resp = await window.openai.callTool("emlakjet.search_listings", {
        path: normalizedPath,
        ...(normalizedFilters ? { filters: normalizedFilters } : {}),
        ...(normalizedSort ? { sort: normalizedSort } : {}),
        page: 1,
        limit: LIMITS.SEARCH_RESULTS_INITIAL,
      });

      const structured = extractStructuredContent(resp);
      if (structured?.kind === "listings") {
        return {
          structured,
          normalizedPath,
          normalizedFilters,
        };
      }
      return null;
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : ERROR_MESSAGES.SEARCH_FAILED;
      setCallError(errorMessage);
      return null;
    } finally {
      setIsCalling(false);
    }
  }, []);

  /**
   * Daha fazla sonuc yukle (sayfalama).
   *
   * @param {Object} query - Mevcut sorgu
   * @param {string} query.path - Arama yolu
   * @param {string} [query.filters] - Filtre string'i
   * @param {number} page - Mevcut sayfa numarasi
   * @param {string} [sort] - Siralama
   * @returns {Promise<Object|null>} Yapilandirilmis sonuc veya null
   */
  const executeLoadMore = useCallback(async (query, page, sort) => {
    if (!isCallToolAvailable()) {
      setCallError(ERROR_MESSAGES.CALL_TOOL_UNAVAILABLE);
      return null;
    }

    const currentPath = query?.path;
    const currentFilters = query?.filters;
    const nextPage = page + 1;

    if (!currentPath) {
      setCallError(ERROR_MESSAGES.PATH_REQUIRED);
      return null;
    }

    const normalizedSort =
      typeof sort === "string" && sort.trim().length > 0
        ? sort.trim()
        : undefined;

    setIsLoadingMore(true);
    setCallError(null);

    try {
      const resp = await window.openai.callTool("emlakjet.search_listings", {
        path: currentPath,
        ...(currentFilters ? { filters: currentFilters } : {}),
        ...(normalizedSort ? { sort: normalizedSort } : {}),
        page: nextPage,
        limit: LIMITS.SEARCH_RESULTS_LOAD_MORE,
      });

      const structured = extractStructuredContent(resp);
      if (structured?.kind === "listings" && Array.isArray(structured.listings)) {
        return structured;
      }
      return null;
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : ERROR_MESSAGES.LOAD_MORE_FAILED;
      setCallError(errorMessage);
      return null;
    } finally {
      setIsLoadingMore(false);
    }
  }, []);

  /**
   * Bolgesel fiyat bilgisi getir.
   *
   * @param {string} path - Arama yolu
   * @param {string} [filters] - Filtre string'i
   * @returns {Promise<Object|null>} Bolgesel fiyat bilgisi veya null
   */
  const executeRegionPrice = useCallback(async (path, filters) => {
    if (!isCallToolAvailable()) {
      setCallError(ERROR_MESSAGES.CALL_TOOL_UNAVAILABLE);
      return null;
    }

    const normalizedPath = typeof path === "string" ? path.trim() : null;
    if (!normalizedPath) {
      setCallError(ERROR_MESSAGES.PATH_REQUIRED);
      return null;
    }

    setRegionLoading(true);
    setCallError(null);

    try {
      const resp = await window.openai.callTool("emlakjet.get_region_price_info", {
        path: normalizedPath,
        filters,
      });

      const structured = extractStructuredContent(resp);
      if (structured?.kind === "region_price") {
        return structured;
      }
      return null;
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : ERROR_MESSAGES.REGION_PRICE_FAILED;
      setCallError(errorMessage);
      return null;
    } finally {
      setRegionLoading(false);
    }
  }, []);

  return {
    isCalling,
    callError,
    setCallError,
    isLoadingMore,
    regionLoading,
    executeSearch,
    executeLoadMore,
    executeRegionPrice,
  };
}

export default useSearchAPI;
